import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Calendar, User, Award, Eye, Clock } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/Loading';
import { eventsService } from '../../services';
import { ChevronLeft } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

const AdminProofs = () => {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProof, setSelectedProof] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadPendingProofs();
  }, []);

  const loadPendingProofs = async () => {
    try {
      setLoading(true);
      const response = await eventsService.getPendingProofs();
      setProofs(response.participations || []);
      setError('');
    } catch (err) {
      console.error('Error loading proofs:', err);
      setError(err.response?.data?.error || 'Erro ao carregar provas pendentes');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (participationId, approved, reason = null) => {
    setReviewing(true);
    try {
      await eventsService.reviewProof(participationId, { approved, reason });
      await loadPendingProofs();
      setShowModal(false);
      setSelectedProof(null);
      setRejectionReason('');
    } catch (err) {
      console.error('Error reviewing proof:', err);
      setError(err.response?.data?.error || 'Erro ao revisar prova');
    } finally {
      setReviewing(false);
    }
  };

  const openModal = (proof) => {
    setSelectedProof(proof);
    setShowModal(true);
    setRejectionReason('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.location.href = '/app/admin'}
                className="flex items-center justify-center w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-1">
                  📸 Provas de Presença
                </h1>
                <p className="text-gray-300">{proofs.length > 0 
                ? `${proofs.length} ${proofs.length === 1 ? 'prova pendente' : 'provas pendentes'}` 
                : 'Analise e aprove as provas de presença enviadas pelos jogadores'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{proofs.length}</p>
                <p className="text-sm text-gray-400">Provas Pendentes</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {proofs.reduce((sum, p) => sum + (p.event?.points || 0), 0)}
                </p>
                <p className="text-sm text-gray-400">Pontos Pendentes</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {new Set(proofs.map(p => p.userId)).size}
                </p>
                <p className="text-sm text-gray-400">Jogadores Únicos</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-6 backdrop-blur-sm">
            <div className="flex items-center">
              <span className="text-red-400 text-3xl mr-4">⚠️</span>
              <div className="flex-1">
                <h3 className="text-red-400 font-semibold mb-1">Erro ao carregar provas</h3>
                <p className="text-red-300 text-sm">{error}</p>
              </div>
              <button
                onClick={loadPendingProofs}
                className="ml-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-lg text-red-400 hover:text-red-300 transition-all duration-300"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Proofs List */}
            {proofs.length === 0 ? (
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-12 border border-white/10 shadow-xl text-center">
                <div className="text-6xl mb-4">📸</div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Nenhuma prova pendente
                </h3>
                <p className="text-gray-400">
                  Todas as provas foram analisadas
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {proofs.map((proof) => (
                  <div 
                    key={proof.id}
                    className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl"
                  >
                    <div className="space-y-4">
                      {/* Event Info */}
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {proof.event?.name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(proof.event?.startDate).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="flex items-center gap-1 text-amber-400 font-medium">
                            <Award className="w-4 h-4" />
                            +{proof.event?.points} pts
                          </span>
                        </div>
                      </div>

                      {/* User Info */}
                      <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-300">Jogador</span>
                          <span className="text-sm text-white font-medium">{proof.user?.name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-300">Personagem</span>
                          <span className="text-sm text-white font-medium">{proof.character?.name}</span>
                        </div>
                      </div>

                      {/* Proof Image */}
                      <div>
                        <img
                          src={getImageUrl(proof.proofImageUrl)}
                          alt="Prova de presença"
                          className="w-full h-48 object-cover rounded-lg border border-white/10 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => openModal(proof)}
                          onError={(e) => {
                            console.error('Erro ao carregar imagem:', proof.proofImageUrl);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZW0gbsOjbyBlbmNvbnRyYWRhPC90ZXh0Pjwvc3ZnPg==';
                          }}
                        />
                      </div>

                      {/* Submission Date */}
                      <div className="text-xs text-gray-500">
                        📅 Enviado em: {new Date(proof.proofSubmittedAt).toLocaleString('pt-BR')}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={() => openModal(proof)}
                          className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          Visualizar
                        </button>
                        <button
                          onClick={() => handleReview(proof.id, true)}
                          disabled={reviewing}
                          className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 border border-green-500/30 rounded-lg text-white transition-all duration-300 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Aprovar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Review Modal */}
        {showModal && selectedProof && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  📸 Revisar Prova
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedProof(null);
                    setRejectionReason('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                  disabled={reviewing}
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Event Details */}
                <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {selectedProof.event?.name}
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Jogador:</span>
                      <span className="text-white ml-2 font-medium">{selectedProof.user?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Personagem:</span>
                      <span className="text-white ml-2 font-medium">{selectedProof.character?.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Data do Evento:</span>
                      <span className="text-white ml-2">
                        {new Date(selectedProof.event?.startDate).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Pontos:</span>
                      <span className="text-amber-400 ml-2 font-semibold">
                        +{selectedProof.event?.points} pts
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400">📅 Enviado em:</span>
                      <span className="text-white ml-2">
                        {new Date(selectedProof.proofSubmittedAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Proof Image */}
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Imagem da Prova</h4>
                  <img
                    src={getImageUrl(selectedProof.proofImageUrl)}
                    alt="Prova de presença"
                    className="w-full max-h-[500px] object-contain rounded-xl border border-white/10 bg-slate-800"
                    onError={(e) => {
                      console.error('Erro ao carregar imagem:', selectedProof.proofImageUrl);
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZW0gbsOjbyBlbmNvbnRyYWRhPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                </div>

                {/* Rejection Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Motivo da Rejeição (opcional)
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Descreva o motivo caso rejeite a prova..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 hover:border-purple-500/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-300"
                    disabled={reviewing}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedProof(null);
                      setRejectionReason('');
                    }}
                    disabled={reviewing}
                    className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleReview(selectedProof.id, false, rejectionReason)}
                    disabled={reviewing}
                    className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 border border-red-500/30 rounded-lg text-white transition-all duration-300 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reviewing ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <XCircle className="w-4 h-4" />
                        Rejeitar
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleReview(selectedProof.id, true)}
                    disabled={reviewing}
                    className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 border border-green-500/30 rounded-lg text-white transition-all duration-300 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reviewing ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Aprovar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProofs;
